import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { FaShoppingCart, FaStar, FaShoppingBag } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import ScrollToTop from "../components/ScrollToTop.jsx";
import { obtenerProductos } from "../services/productosService.js";

export default function Productos() {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("Todos");
  const { addToCart, isInCart, getItemQuantity } = useCart();

  const categorias = [
    "Todos",
    "Ropa",
    "Accesorios",
    "Tarot",
    "Amuletos",
    "Velas Aromáticas",
    "Cristales",
    "Inciensos",
    "Péndulos",
    "Otros",
  ];

  useEffect(() => {
    const cargarProductos = async () => {
      try {
        setCargando(true);
        const data = await obtenerProductos();
        setProductos(data);
      } catch (error) {
        console.error("Error al cargar productos:", error);
        setError("Hubo un problema al cargar los productos.");
      } finally {
        setCargando(false);
      }
    };

    cargarProductos();
  }, []);

  const productosFiltrados =
    categoriaSeleccionada === "Todos"
      ? productos
      : productos.filter((p) => p.categoria === categoriaSeleccionada);

  if (cargando)
    return (
      <>
        <Helmet>
          <title>Productos - La CLO Brand</title>
          <meta
            name="description"
            content="Cargando catálogo de productos..."
          />
        </Helmet>
        <div
          className="main-container"
          style={{ textAlign: "center", fontSize: "1.5rem", padding: "4rem 0" }}
        >
          <p className="glow-text" style={{ color: "var(--accent-purple)" }}>
            ✨ Cargando productos mágicos... ✨
          </p>
        </div>
      </>
    );

  if (error)
    return (
      <div
        className="main-container"
        style={{
          textAlign: "center",
          fontSize: "1.2rem",
          color: "var(--accent-rose)",
          padding: "4rem 0",
        }}
      >
        <p>{error}</p>
        <button
          className="btn-mystical"
          onClick={() => window.location.reload()}
          style={{ marginTop: "2rem" }}
        >
          Reintentar
        </button>
      </div>
    );

  const handleAddToCart = (producto) => {
    addToCart(producto);
  };

  return (
    <div className="main-container">
      <Helmet>
        <title>Catálogo de Productos - La CLO Brand | Moda y Accesorios</title>
        <meta
          name="description"
          content={`Explora nuestro catálogo de ${productos.length} productos únicos: prendedores, accesorios, joyería artesanal y más. Diseños exclusivos para tu estilo.`}
        />
        <meta
          name="keywords"
          content="catálogo moda, prendedores, brooches, accesorios de moda, joyería artesanal, comprar online, La CLO Brand"
        />
        <meta
          property="og:title"
          content="Catálogo de Productos - La CLO Brand"
        />
        <meta
          property="og:description"
          content={`Descubre nuestra colección de ${productosFiltrados.length} productos únicos y exclusivos.`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.laclobrand.com.ar/Productos"
        />
        <link rel="canonical" href="https://www.laclobrand.com.ar/Productos" />
      </Helmet>

      {/* Header de la página */}
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "3rem",
            background: "var(--gradient-soft)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "var(--white)",
            marginBottom: "1rem",
            fontWeight: "700",
            paddingTop: "2rem",
          }}
        >
          ✨ Tienda Mágica 🔮
        </h1>
        <p
          style={{
            fontSize: "1.3rem",
            color: "var(--slate-200)",
            fontFamily: "var(--font-body)",
            fontWeight: "400",
          }}
        >
          Descubre productos místicos para tu bienestar espiritual
        </p>
      </div>

      {/* Filtro de Categorías */}
      <div
        style={{
          marginBottom: "2rem",
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
          justifyContent: "center",
          padding: "0 1rem",
        }}
      >
        {categorias.map((categoria) => (
          <button
            key={categoria}
            onClick={() => setCategoriaSeleccionada(categoria)}
            className={
              categoriaSeleccionada === categoria
                ? "category-btn active"
                : "category-btn"
            }
            style={{
              padding: "0.7rem 1.5rem",
              borderRadius: "25px",
              cursor: "pointer",
              fontSize: "0.95rem",
              transition: "all 0.3s ease",
              fontFamily: "var(--font-body)",
            }}
          >
            {categoria}
          </button>
        ))}
      </div>

      {/* Contador de productos */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "2rem",
          color: "var(--slate-300)",
          fontSize: "1.1rem",
        }}
      >
        {categoriaSeleccionada === "Todos"
          ? `Mostrando todos los productos (${productos.length})`
          : `${productosFiltrados.length} producto${
              productosFiltrados.length !== 1 ? "s" : ""
            } en ${categoriaSeleccionada}`}
      </div>

      {/* Grid de productos */}
      {productosFiltrados.length === 0 ? (
        <div style={{ textAlign: "center", padding: "4rem 0" }}>
          <p style={{ fontSize: "1.2rem", color: "var(--slate-400)" }}>
            {categoriaSeleccionada === "Todos"
              ? "No se encontraron productos disponibles."
              : `No hay productos en la categoría "${categoriaSeleccionada}".`}
          </p>
        </div>
      ) : (
        <ul id="lista-productos">
          {productosFiltrados.map((producto) => {
            const itemQuantity = getItemQuantity(producto.id);
            const inCart = isInCart(producto.id);

            return (
              <li key={producto.id}>
                {/* Imagen del producto */}
                <div
                  style={{
                    height: "220px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "15px",
                    marginBottom: "1.5rem",
                    border: "2px solid var(--accent-purple)",
                    boxShadow: "var(--shadow-soft)",
                    backgroundColor: "rgba(15, 23, 42, 0.3)",
                    padding: "10px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={producto.avatar}
                    alt={producto.nombre}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "10px",
                      transition: "all 0.4s ease",
                      filter: "brightness(0.95) contrast(1.05)",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = "scale(1.05)";
                      e.target.style.filter = "brightness(1.1) contrast(1.15)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "scale(1)";
                      e.target.style.filter = "brightness(0.95) contrast(1.05)";
                    }}
                  />
                </div>

                {/* Información del producto */}
                <h3
                  style={{
                    color: "var(--accent-purple)",
                    marginBottom: "0.5rem",
                    fontFamily: "var(--font-heading)",
                    fontSize: "1.5rem",
                    fontWeight: "600",
                  }}
                >
                  {producto.nombre}
                </h3>

                {/* Badge de categoría */}
                {producto.categoria && (
                  <div style={{ marginBottom: "1rem" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "0.3rem 0.8rem",
                        borderRadius: "15px",
                        background: "var(--gradient-soft)",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        color: "var(--white)",
                        fontFamily: "var(--font-body)",
                      }}
                    >
                      {producto.categoria}
                    </span>
                  </div>
                )}

                <p
                  style={{
                    color: "var(--slate-200)",
                    margin: "1rem 0",
                    fontSize: "1.1rem",
                    lineHeight: "1.6",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {producto.descripcion}
                </p>

                {/* Precio */}
                <div
                  style={{
                    fontSize: "1.4rem",
                    fontWeight: "bold",
                    color: "var(--accent-amber)",
                    margin: "1.5rem 0",
                    fontFamily: "var(--font-heading)",
                  }}
                >
                  ${producto.precio}
                </div>

                {/* Botones de acción */}
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {inCart ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <span
                        style={{
                          color: "var(--accent-teal)",
                          fontWeight: "600",
                          fontSize: "1rem",
                        }}
                      >
                        En carrito: {itemQuantity}
                      </span>
                      <button
                        className="btn-mystical"
                        onClick={() => handleAddToCart(producto)}
                        style={{
                          padding: "0.5rem 1rem",
                          fontSize: "0.9rem",
                        }}
                      >
                        + Agregar más
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-mystical"
                      onClick={() => handleAddToCart(producto)}
                      style={{
                        marginBottom: "1rem",
                        background: "var(--gradient-accent)",
                        fontWeight: "600",
                      }}
                    >
                      <FaShoppingCart style={{ marginRight: "0.5rem" }} />
                      Agregar al carrito
                    </button>
                  )}

                  <Link to={`/Productos/${producto.id}`} state={{ producto }}>
                    <button
                      className="btn-mystical"
                      style={{
                        background: "var(--gradient-soft)",
                        fontSize: "0.95rem",
                      }}
                    >
                      👁️ Ver detalles
                    </button>
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ScrollToTop />
    </div>
  );
}
